import type { Ref } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import type { AllEntities, BaseEntity } from 'c-sdk';
import { getBaseSliceSelectors } from 'c-data-layer';
import type { OptionSchema } from 'c-components/Forms/formTypes';
import type { AutocompleteProps } from 'c-components/Forms/Autocomplete';
import Autocomplete from 'c-components/Forms/Autocomplete';
import { useAPIClientRequest } from 'c-hooks';
import apiClient from 'c-data/apiClient';
import { useAllEntityData } from 'c-data';

type Props<Entity extends BaseEntity> = {
    entityName: keyof AllEntities;
    labelField: keyof Entity | string;
    valueField?: keyof Entity | string;
    label?: string | React.ReactNode;
    multi?: boolean;
    value: number | number[];
    onChange: (newValue: number | number[]) => void;
    disabled?: boolean;
    additionalOptions?: OptionSchema[];
    useLocalEntityData?: boolean;
    size?: AutocompleteProps['size'];
    filter?: Record<string, string[] | number[]>;
    includes?: string[];
    loadingDisable?: boolean;
    labelFormatter?: (entity: any) => string;
};

function AllEntitiesDropdown<Entity extends BaseEntity>(
    {
        entityName,
        labelField,
        valueField = 'id',
        label,
        multi = false,
        value,
        onChange,
        disabled = false,
        additionalOptions = [],
        useLocalEntityData = false,
        size,
        filter = {},
        includes = [],
        loadingDisable = false,
        labelFormatter,
    }: Props<Entity>,
    ref: Ref<any>,
) {
    const { allEntities, getAll } = useAllEntityData<Entity>(
        useLocalEntityData ? undefined : entityName,
    );
    const [tracker, setTracker] = useState<Record<string, { filter: string; activated: boolean }>>(
        {},
    );
    const filterHash = JSON.stringify({ filter, includes });

    const actuallyUseLocalData = useMemo(
        () => useLocalEntityData || getBaseSliceSelectors(entityName) == null,
        [entityName, useLocalEntityData],
    );
    const { start, data, isLoading } = useAPIClientRequest(apiClient.Entities[entityName].list);

    const entities = useMemo(() => {
        if (!actuallyUseLocalData) return allEntities;
        return data?.data?.data ?? [];
    }, [actuallyUseLocalData, data, allEntities]);

    useEffect(() => {
        const currentTracker = tracker[entityName];
        if (!currentTracker || currentTracker.filter !== filterHash || !currentTracker.activated) {
            if (!actuallyUseLocalData) getAll(undefined, true);
            else start({ perPage: 99999, filters: filter, includes });
            setTracker(prev => ({
                ...prev,
                [entityName]: { filter: filterHash, activated: true },
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);
    const options = useMemo<OptionSchema[]>(
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        () => [
            ...additionalOptions,
            ...(entities.map(entity => ({
                value: entity[valueField],
                label: labelFormatter ? labelFormatter(entity) : (entity[labelField] as string),
            })) ?? []),
        ],
        [additionalOptions, entities, valueField, labelFormatter, labelField],
    );

    return (
        <Autocomplete
            ref={ref}
            value={value}
            onChange={onChange}
            textFieldProps={{
                label,
            }}
            options={options}
            multiple={multi}
            disabled={disabled || isLoading}
            size={size}
        />
    );
}

export default React.forwardRef(AllEntitiesDropdown);
